/**
 * Servicio optimizado para obtener solo los conteos necesarios para PlanIndicator
 * Evita las múltiples llamadas API que hace useEmpresaStats
 */
import apiClient from './apiClient';
import moment from 'moment';
import { API_ENDPOINTS } from '../config/api';

/**
 * Obtiene solo los conteos necesarios para el PlanIndicator
 * @param {string} empresaId - ID de la empresa
 * @param {string} sucursalId - ID de la sucursal seleccionada
 * @returns {Object} Objeto con conteos simples
 */
export const getPlanIndicatorStats = async (empresaId, sucursalId) => {
  try {
    //console.log('📊 [PlanStats] Fetching optimized stats for empresa:', empresaId, 'sucursal:', sucursalId);
    
    // Usar el endpoint de estadísticas optimizado si existe
    const response = await apiClient.get(API_ENDPOINTS.ESTADISTICAS.BASE);
    
    if (response.data?.success) {
      const stats = response.data.data;
      //console.log('📊 [PlanStats] Optimized stats received:', stats);
      
      return {
        sucursales: stats.sucursales.actual || 0,
        vehiculos: stats.vehiculos.actual || 0,
        mantenimientos: stats.mantenimientos.actual || 0,
        clientes: stats.clientes.actual || 0
      };
    }
    
    throw new Error('Invalid response from plan stats API');
  } catch (error) {
    console.warn('⚠️ [PlanStats] Optimized endpoint not available, using fallback:', error.message);
    
    // Fallback: hacer las llamadas mínimas necesarias con COUNT queries
    return await getPlanIndicatorStatsFallback(empresaId, sucursalId);
  }
};

/**
 * Fallback que hace llamadas directas a los endpoints reales
 */
const getPlanIndicatorStatsFallback = async (empresaId, sucursalId) => {
  try {
    //console.log('📊 [PlanStats] Using fallback - making real API calls to get counts');
    
    // Hacer llamadas paralelas a los endpoints reales (sin params especiales)
    const [
      sucursalesResponse,
      vehiculosResponse, 
      mantenimientosResponse,
      clientesResponse
    ] = await Promise.all([
      // Obtener sucursales (filtrado por empresa se hace automáticamente)
      apiClient.get('/api/sucursales').catch(e => ({ data: { data: [] } })),
      
      // Obtener vehículos (todos de la empresa)
      apiClient.get('/api/vehiculos').catch(e => ({ data: { data: [] } })),
      
      // Obtener mantenimientos del mes actual por sucursal
      apiClient.get('/api/mantenimientos', {
        params: { 
          sucursal_id: sucursalId,
          fecha_inicio: moment().startOf('month').format('YYYY-MM-DD'),
          fecha_fin: moment().endOf('month').format('YYYY-MM-DD')
        }
      }).catch(e => ({ data: { data: [] } })),
      
      // Obtener clientes (todos de la empresa)
      apiClient.get('/api/clientes').catch(e => ({ data: { data: [] } }))
    ]);
    
    // Extraer conteos de las respuestas reales
    const extractCount = (response) => {
      if (response.data?.count !== undefined) return response.data.count;
      if (response.data?.data) return response.data.data.length;
      if (response.data?.vehiculos) return response.data.vehiculos.length;
      if (response.data?.clientes) return response.data.clientes.length;
      if (response.data?.mantenimientos) return response.data.mantenimientos.length;
      if (response.data?.sucursales) return response.data.sucursales.length;
      if (Array.isArray(response.data)) return response.data.length;
      return 0;
    };
    
    const stats = {
      sucursales: extractCount(sucursalesResponse),
      vehiculos: extractCount(vehiculosResponse),
      mantenimientos: extractCount(mantenimientosResponse),
      clientes: extractCount(clientesResponse)
    };
    
    console.log('📊 [PlanStats] Fallback stats extracted:', {
      sucursales: `${stats.sucursales} (from response)`,
      vehiculos: `${stats.vehiculos} (from response)`,
      mantenimientos: `${stats.mantenimientos} (from response)`,
      clientes: `${stats.clientes} (from response)`
    });
    
    return stats;
    
  } catch (error) {
    console.error('❌ [PlanStats] Fallback failed:', error);
    
    // Retornar valores por defecto en caso de error
    return {
      sucursales: 0,
      vehiculos: 0,
      mantenimientos: 0,
      clientes: 0
    };
  }
};

export default {
  getPlanIndicatorStats
};